import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { CHAPTERS, IMAGES } from "@/data/content";

const Manifesto = () => {
  const [active, setActive] = useState(0);

  return (
    <section id="manifesto" data-testid="manifesto-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead
              eyebrow="The Manifesto — 3 Chapters"
              title="Everything we believe, in three lines of attack."
              testId="manifesto-heading"
            />
            <Reveal delay={0.15} className="mt-10 hidden lg:block">
              <div className="relative aspect-[4/5] overflow-hidden border border-[#27272A]">
                <img
                  src={IMAGES.manifestoSpot}
                  alt="Focused man in dramatic low light"
                  className="h-full w-full object-cover grayscale"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/90 via-transparent to-transparent" />
                <p className="absolute bottom-4 left-4 font-jb text-[10px] uppercase tracking-[0.22em] text-zinc-400">
                  Fig. 02 — The standard
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            {CHAPTERS.map((ch, i) => {
              const isOpen = active === i;
              return (
                <Reveal key={ch.num} delay={i * 0.08}>
                  <div
                    className={`border-t border-[#27272A] ${i === CHAPTERS.length - 1 ? "border-b" : ""}`}
                  >
                    <button
                      data-testid={`manifesto-chapter-${ch.num}`}
                      onClick={() => setActive(isOpen ? -1 : i)}
                      className="flex w-full items-center gap-6 py-8 text-left sm:gap-10"
                    >
                      <span
                        className={`font-syne text-5xl font-extrabold transition-colors duration-500 sm:text-7xl ${
                          isOpen ? "text-[#CC0A14]" : "text-outline"
                        }`}
                      >
                        {ch.num}
                      </span>
                      <span
                        className={`flex-1 font-syne text-xl font-bold tracking-tight transition-colors duration-300 sm:text-2xl ${
                          isOpen ? "text-white" : "text-zinc-400"
                        }`}
                      >
                        {ch.title}
                      </span>
                      <motion.span
                        animate={{ rotate: isOpen ? 45 : 0 }}
                        transition={{ duration: 0.35 }}
                        className={isOpen ? "text-[#CC0A14]" : "text-zinc-600"}
                      >
                        <Plus size={22} />
                      </motion.span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 gap-8 pb-10 sm:grid-cols-3 sm:pl-[7.5rem]">
                            <p className="text-sm leading-relaxed text-zinc-400 sm:col-span-2 sm:text-base">
                              {ch.body}
                            </p>
                            <div className="border border-[#7F1D1D]/40 bg-[#121214] p-5">
                              <p className="font-syne text-4xl font-extrabold text-[#CC0A14]">
                                {ch.stat}
                              </p>
                              <p className="mt-2 font-jb text-[10px] uppercase leading-relaxed tracking-[0.18em] text-zinc-500">
                                {ch.statLabel}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
